import ScreenWrapper from "@/components/ScreenWrapper"
import {
  createFileRoute,
  useNavigate,
  useRouter,
  useSearch,
} from "@tanstack/react-router"
import {
  DataTable,
  TABLE_PAGE_SIZE,
  selectColumn,
  createSortableColumn,
  usePaginatedTableWithUrlState,
  createTableSearchSchema,
  createCustomColumn,
  getFullName,
  OrganizationUser,
  getTranslatedOrganizationRoles,
  TableActionsDropdown,
  EditOrganizationUserRolesModal,
} from "toptaal-shared"
import {
  getFilteredOrganizationUsers,
  updateOrganizationUser,
} from "@/api/services/organizationUser"
import type { ColumnDef } from "@tanstack/react-table"
import { organizationUserQueryKeys } from "@/api/query-keys/organizations"
import { TableHeaderActions } from "@/components/TableHeaderActions"
import { useState } from "react"
import { organizationUserCan } from "@/utils/organizationUserCan"
import { EditOrganizationUserInfoModal } from "@/components/modals/organizationUser/EditOrganizationUserInfoModal"

export const Route = createFileRoute("/beheer/medewerkers/")({
  component: RouteComponent,
  validateSearch: createTableSearchSchema(),
})

function RouteComponent() {
  const router = useRouter()

  const searchParams = useSearch({ from: "/beheer/medewerkers/" })
  const navigate = useNavigate({ from: "/beheer/medewerkers/" })
  const [activeRow, setActiveRow] = useState<OrganizationUser | null>(null)
  const [editOrganizationUserRoleOpen, setEditOrganizationUserRoleOpen] =
    useState(false)
  const [editOrganizationUserInfoOpen, setEditOrganizationUserInfoOpen] =
    useState(false)

  const {
    isFetching,
    data,
    search,
    setSearch,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    pagination,
    columnVisibility,
    setColumnVisibility,
  } = usePaginatedTableWithUrlState({
    queryKey: organizationUserQueryKeys.all,
    queryFn: getFilteredOrganizationUsers,
    pageSize: TABLE_PAGE_SIZE,
    searchParams,
    navigate,
    storageKey: "/beheer/medewerkers",
  })

  const getOrganizationUserActions = (user: OrganizationUser) => [
    {
      label: "Gegevens bewerken",
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation()
        setActiveRow(user)
        setEditOrganizationUserInfoOpen(true)
      },
    },
    {
      label: "Rollen beheren",
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation()
        setActiveRow(user)
        setEditOrganizationUserRoleOpen(true)
      },
    },
  ]

  const columns: ColumnDef<OrganizationUser>[] = [
    selectColumn(),
    createSortableColumn({ accessorKey: "id", name: "ID" }),
    createCustomColumn({
      id: "first_name",
      name: "Naam",
      cell: ({ row }: { row: { original: OrganizationUser } }) =>
        getFullName({
          firstName: row.original.firstName,
          namePrefix: row.original.namePrefix,
          lastName: row.original.lastName,
        }),
      accessorKey: "firstName",
    }),
    createSortableColumn({ accessorKey: "function", name: "Functie" }),
    createCustomColumn({
      id: "roles",
      name: "Rollen",
      cell: ({ row }: { row: { original: OrganizationUser } }) =>
        getTranslatedOrganizationRoles(row.original.roles),
      accessorKey: "roles",
      enableSorting: false,
    }),
    createSortableColumn({ accessorKey: "email", name: "E-mailadres" }),
    createSortableColumn({ accessorKey: "phoneNumber", name: "Tel. nr." }),

    ...(organizationUserCan("update", "contacts")
      ? [
          createCustomColumn<OrganizationUser>({
            id: "actions",
            name: "",
            cell: ({ row }) => {
              return (
                <TableActionsDropdown
                  row={row}
                  actions={getOrganizationUserActions(row.original)}
                />
              )
            },
            enableSorting: false,
            enableHiding: false,
          }),
        ]
      : []),
  ]

  const handleNavigateToDetail = (
    organizationUserId: OrganizationUser["id"],
  ) => {
    router.navigate({
      to: "/beheer/medewerkers/$organizationUserId/gegevens",
      params: { organizationUserId: organizationUserId.toString() },
    })
  }

  return (
    <ScreenWrapper showManagementMenu>
      <DataTable
        title="Medewerkers"
        data={data?.data.users || []}
        isLoading={isFetching}
        pagination={pagination}
        sortDirection={sortDirection}
        sortField={sortField}
        search={search}
        setSearch={setSearch}
        setSortField={setSortField}
        setSortDirection={setSortDirection}
        columnVisibility={columnVisibility}
        setColumnVisibility={setColumnVisibility}
        onRowClick={handleNavigateToDetail}
        columns={columns}
        headerRightActions={
          <>
            <TableHeaderActions
              createAction={{
                label: "Nieuwe medewerker",
                to: "/beheer/medewerkers/aanmaken",
                entity: "contacts",
                permissions: "create",
              }}
            />
          </>
        }
      />

      {activeRow && (
        <EditOrganizationUserRolesModal
          isOpen={editOrganizationUserRoleOpen}
          setIsOpen={setEditOrganizationUserRoleOpen}
          activeRow={activeRow}
          listQueryKey={organizationUserQueryKeys.all}
          userQueryKey={organizationUserQueryKeys.user(activeRow.id)}
          updateOrganizationUserFn={updateOrganizationUser}
          invalidateFilters={(queryClient) => {
            queryClient.invalidateQueries({
              queryKey: organizationUserQueryKeys.all,
            })
          }}
        />
      )}

      {activeRow && (
        <EditOrganizationUserRolesModal
          isOpen={editOrganizationUserRoleOpen}
          setIsOpen={setEditOrganizationUserRoleOpen}
          activeRow={activeRow}
          listQueryKey={organizationUserQueryKeys.all}
          userQueryKey={organizationUserQueryKeys.user(activeRow.id)}
          updateOrganizationUserFn={updateOrganizationUser}
          invalidateFilters={(queryClient) => {
            queryClient.invalidateQueries({
              queryKey: organizationUserQueryKeys.all,
            })
          }}
        />
      )}

      {activeRow && (
        <EditOrganizationUserInfoModal
          isOpen={editOrganizationUserInfoOpen}
          setIsOpen={setEditOrganizationUserInfoOpen}
          handleSuccess={() => {
            setEditOrganizationUserInfoOpen(false)
          }}
          activeRow={activeRow}
        />
      )}
    </ScreenWrapper>
  )
}
